import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';

import type { Options } from 'highcharts';

import {
  InventoryHelper,
  InventoryProvider,
  MappingCollections,
  SensorInventory,
} from '@scaleaq/sitecontrol-lib';
import HighchartsReact from 'highcharts-react-official';
import { DateTime, Duration, Interval } from 'luxon';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { useGetMeasurementTypes, useGetSensorsAndAncestors } from '@/dataAccess/rest';
import { usePeriodicCallback } from '@/hooks';
import { capitalizeAndSplitOnUpperCase, groupSensorOptionsByType, toApiCalendarDay } from '@/utils';

import { useGetFriendlyProviderName } from '../../hooks/';
import { useSiteContext } from '../../providers';
import { defaultChartOptions, getChartOptionsWithLocale } from './chart/defaultHighChartOptions.ts';
import { chartIntervalValueToKey } from './chartHelperFunctions/chartIntervalValueToKey.ts';
import { chartTypeValueToKey } from './chartHelperFunctions/chartTypeValueToKey.ts';
import { ChartInterval, ChartType, intervalToDuration } from './types.ts';

/**
 * Hook that fetches measurement types for the given sensors and returns plot type options.
 * When multiple sensor IDs are provided, only plot types supported by all sensors are returned.
 *
 * @param sensorIds - Optional list of sensor IDs to fetch measurement types for
 * @returns Plot type options (id, value, label) and loading state
 */
export const useChartPlotTypes = (sensorIds?: string[]) => {
  const { data: types, isLoading } = useGetMeasurementTypes(sensorIds);

  const plotTypes = useMemo(() => {
    const classNameToFriendlyProviderName = MappingCollections.classNameToFriendlyProviderName;
    const typesArray = Object.values(types ?? {});

    if (!typesArray.length || typesArray.every(arr => !arr.length)) return [];

    const plotTypesArray =
      typesArray.length === 1
        ? typesArray[0]
        : typesArray[0].filter(
            type => !typesArray.slice(1).some(typeList => !typeList.includes(type))
          );

    return plotTypesArray.map(type => ({
      id: uuidv4(),
      value: type,
      label: classNameToFriendlyProviderName[type] ?? capitalizeAndSplitOnUpperCase(type),
    }));
  }, [types]);

  return { plotTypes, isLoading };
};

/**
 * Hook that fetches sensors and builds sensor and provider select options.
 * Sensors are filtered by optional root ID and selected providers. Provider options
 * are limited to numerical providers and filtered by selected sensors; when multiple sensors are selected, only providers
 * supported by all selected sensors are returned.
 *
 * @param selectedSensorIds - Currently selected sensor IDs
 * @param selectedProviders - Currently selected inventory providers
 * @param rootId - Optional root ID to restrict sensors to those under this path
 * @returns Sensor options grouped by type, provider options, and loading state
 */
export const useSensorsAndProviders = (
  selectedSensorIds: string[] | undefined,
  selectedProviders: InventoryProvider[],
  rootId?: string
) => {
  const { currentSite: { id: siteInventoryId } = {} } = useSiteContext();
  const { data: sensors, isLoading } = useGetSensorsAndAncestors({
    inventoryId: siteInventoryId,
  });
  const { getFriendlyProviderName } = useGetFriendlyProviderName(true);

  const sensorOptions = useMemo(
    () =>
      groupSensorOptionsByType(
        sensors
          ?.filter(sensor => sensorRootFilter(sensor, rootId))
          .filter(sensor => sensorProviderFilter(sensor, selectedProviders)) ?? [],
        sensor => sensor.id
      ),
    [rootId, selectedProviders, sensors]
  );

  const providerOptions = useMemo(() => {
    if (!sensors) return [];

    // No selected sensors -> All numerical providers returned
    if (!selectedSensorIds?.length) {
      const providerOptions = new Set<InventoryProvider>();
      sensors.forEach(sensor =>
        sensor.providers.forEach(provider => {
          if (InventoryHelper.numericalProviders.includes(provider)) providerOptions.add(provider);
        })
      );
      return Array.from(providerOptions).map(provider =>
        providerToSelectOption(provider, getFriendlyProviderName)
      );
    }

    // A single sensor selected -> All its numerical providers returned
    if (selectedSensorIds.length === 1)
      return (
        sensors
          .find(sensor => sensor.id === selectedSensorIds[0])
          ?.providers.filter(provider => InventoryHelper.numericalProviders.includes(provider))
          .map(provider => providerToSelectOption(provider, getFriendlyProviderName)) ?? []
      );

    // Multiple sensors selected -> The intersection of their numerical providers returned
    const providerArrays = sensors
      .filter(sensor => selectedSensorIds.includes(sensor.id))
      .map(sensor =>
        sensor.providers.filter(provider => InventoryHelper.numericalProviders.includes(provider))
      );

    if (!providerArrays.length) return [];

    const initialProviders = providerArrays[0];
    const filterProviders = providerArrays.slice(1);

    // Filter out providers that do not exist in every selected sensor's list of providers
    return initialProviders
      .filter(provider => filterProviders.every(providerArray => providerArray.includes(provider)))
      .map(provider => providerToSelectOption(provider, getFriendlyProviderName));
  }, [getFriendlyProviderName, selectedSensorIds, sensors]);

  return {
    sensorOptions,
    providerOptions,
    isLoading,
  };
};

/**
 * Hook that builds chart type and chart interval options for the chart configuration UI.
 * Interval options can be constrained by optional min and max interval bounds.
 *
 * @param intervalExtremes - Optional min and max chart interval to include in interval options
 * @returns Chart type options and chart interval options with translated labels
 */
export const useLiveChartOptions = (intervalExtremes?: {
  minInterval?: ChartInterval;
  maxInterval?: ChartInterval;
}) => {
  const { t } = useTranslation(['chartConfig']);
  const minInterval = intervalExtremes?.minInterval;
  const maxInterval = intervalExtremes?.maxInterval;
  const chartTypeOptions = useMemo(
    () =>
      Object.values(ChartType).map(v => ({
        id: v,
        value: v,
        label: t(`chartConfig:chartType.${chartTypeValueToKey[v]}`),
      })),
    [t]
  );

  const chartIntervalOptions = useMemo(
    () =>
      Object.values(ChartInterval)
        .filter(
          v =>
            (minInterval === undefined ||
              Duration.fromObject(intervalToDuration[v]) >=
                Duration.fromObject(intervalToDuration[minInterval])) &&
            (maxInterval === undefined ||
              Duration.fromObject(intervalToDuration[v]) <=
                Duration.fromObject(intervalToDuration[maxInterval]))
        )
        .map(v => ({
          id: v,
          value: v,
          label: t(`chartConfig:chartInterval.${chartIntervalValueToKey[v]}`),
        })),
    [maxInterval, minInterval, t]
  );

  return { chartTypeOptions, chartIntervalOptions };
};

/**
 * Hook that creates Highcharts options with the current locale.
 * This ensures the chart is initialized with the correct locale,
 * preventing locale issues in production where charts might initialize
 * before the locale is set. The options are memoized and update when the language changes.
 *
 * @param baseOptions - Optional base Highcharts options to extend (defaults to defaultChartOptions)
 * @returns Highcharts options with locale set
 */
export const useChartOptionsWithLocale = (baseOptions: Options = defaultChartOptions): Options => {
  const { i18n } = useTranslation();
  return useMemo(
    () => getChartOptionsWithLocale(baseOptions, i18n.language),
    [baseOptions, i18n.language]
  );
};

/**
 * Hook that handles time ranges for charts.
 * This ensures that live charts periodically refetch data, using updated time ranges,
 * keeping the data fresh. The time range is updated at intervals determined by the lice chart's interval,
 * though never less frequently than once per hour.
 * For historic charts, the input time range is returned as is, without any periodic updates,
 * though changes to the input time range are handled.
 *
 * @param chartType - The type of chart (live or historic)
 * @param chartInterval - The interval for the chart data
 * @param defaultTimeRange - Optional default time range for historic charts
 * @param snapToDay - Snap live windows of a day or longer to the start of the API day. Only opt
 * in when the window end is not sent to the API (the query stays open-ended towards now);
 * consumers that send the end would cut today's data off at midnight.
 * @returns Highcharts options with locale set
 */
export const useChartTimeRange = (
  chartType: ChartType,
  chartInterval: ChartInterval,
  defaultTimeRange?: { start: DateTime; end: DateTime },
  snapToDay = false
) => {
  const shouldUpdate = useMemo(() => chartType === ChartType.LIVE, [chartType]);
  const chartIntervalDuration = useMemo(
    () => Duration.fromObject(intervalToDuration[chartInterval]),
    [chartInterval]
  );
  const updateInterval = useMemo(() => {
    const maxIntervalDuration = Duration.fromObject({ hours: 1 });

    return chartIntervalDuration.toMillis() > maxIntervalDuration.toMillis()
      ? maxIntervalDuration
      : chartIntervalDuration;
  }, [chartIntervalDuration]);

  const [timeRange, setTimeRange] = useState<Interval>(() => {
    if (shouldUpdate) return getNewInterval({ updatePeriod: chartIntervalDuration, snapToDay });
    if (defaultTimeRange)
      return Interval.fromDateTimes(defaultTimeRange.start, defaultTimeRange.end);
    return Interval.invalid('Missing default interval');
  });

  // handle updates to historic chart start and end dates.
  useEffect(() => {
    // Do not update if chart is not historic
    if (shouldUpdate) return;

    const newTimeRange = defaultTimeRange
      ? Interval.fromDateTimes(defaultTimeRange.start, defaultTimeRange.end)
      : Interval.invalid('Missing default interval');

    if (!timeRange.equals(newTimeRange)) setTimeRange(newTimeRange);
  }, [defaultTimeRange, shouldUpdate, timeRange]);

  // Keep the previous interval when the recomputed one is equal (e.g. a day-snapped window
  // recomputed hourly), so subscribers are not re-rendered with a new reference every tick.
  const updateTimeRange = useCallback(
    () =>
      shouldUpdate &&
      setTimeRange(previous => {
        const next = getNewInterval({ updatePeriod: chartIntervalDuration, snapToDay });
        return previous.equals(next) ? previous : next;
      }),
    [chartIntervalDuration, shouldUpdate, snapToDay]
  );

  usePeriodicCallback(
    updateTimeRange,
    updateInterval,
    'useChartTimeRange (AggregatedHighChart, ScatterPlotHighChart, BoxPlotHighChart or HistogramHighChart)'
  );

  return { start: timeRange.start, end: timeRange.end, updateInterval };
};

/**
 * Keeps a Highcharts instance synced with its parent container size.
 * GridStack resizes do not always trigger Highcharts reflow automatically.
 */
export const useAutoResizeHighChart = (
  highChartsRef: RefObject<HighchartsReact.RefObject | null>
): void => {
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const chart = highChartsRef.current?.chart;
    const container = chart?.container?.parentElement;
    if (!chart || !container) {
      return;
    }

    const resizeChart = () => {
      chart.setSize(undefined, container.clientHeight, false);
    };

    resizeChart();

    const resizeObserver = new ResizeObserver(() => {
      resizeChart();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [highChartsRef]);
};

/**
 * Hook that fetches sensors and returns a map of sensor ID to sensor name.
 *
 * @returns Record mapping sensor ID to sensor name
 */
export const useSensorNames = () => {
  const { currentSite: { id: siteInventoryId } = {} } = useSiteContext();
  const { data: sensors } = useGetSensorsAndAncestors({ inventoryId: siteInventoryId });

  return useMemo<Record<string, string>>(
    () =>
      sensors
        ? sensors.reduce(
            (acc, sensor) => ({ ...acc, [sensor.id]: sensor.name }),
            {} as Record<string, string>
          )
        : {},
    [sensors]
  );
};

// Helper functions

/**
 * Creates a time interval from (now minus updatePeriod) to now, anchored to the start of the hour
 * in the local zone.
 *
 * With `snapToDay`, windows of a day or longer are snapped to the start of the API (Oslo) day
 * instead, so day-resolution charts request full daily buckets and the query stays stable — and
 * cacheable — until the API day ends. That start is carried as local midnight of the Oslo date, so
 * in zones west of Oslo it can be up to a day ahead of now. This is opt-in because a day-snapped
 * end is not "now": it excludes today's data, or lies in the future; only consumers that leave the
 * query end open towards now can use it. Sub-daily windows always keep the hourly anchor; day
 * alignment would only make them fetch up to a full day of data they never display.
 *
 * @param updatePeriod - Duration to subtract from the anchor for the interval start
 * @param snapToDay - Snap windows of a day or longer to the start of the API day
 * @returns Interval ending at the anchor: the start of the hour, or of the API day when snapped
 */
const getNewInterval = ({
  updatePeriod,
  snapToDay,
}: {
  updatePeriod: Duration;
  snapToDay: boolean;
}): Interval => {
  const now =
    snapToDay && updatePeriod.as('days') >= 1
      ? toApiCalendarDay(DateTime.now())
      : DateTime.now().startOf('hour');
  return Interval.fromDateTimes(now.minus(updatePeriod), now);
};

/**
 * Maps an inventory provider string to a select option with friendly key and class name value.
 *
 * @param provider - The inventory provider identifier
 * @param getFriendlyProviderName
 * @returns Select option with id, key (friendly name), and value (class name)
 */
const providerToSelectOption = (
  provider: string,
  getFriendlyProviderName: (provider: string) => string
) => {
  const className = MappingCollections.inventoryProviderClassPair[provider] ?? '';
  const friendlyProviderName = getFriendlyProviderName(provider);

  return {
    id: uuidv4(),
    label: friendlyProviderName,
    value: className,
  };
};

/**
 * Checks whether a given SensorInventory has any numerical providers, and if any match any of the optionally supplied providers.
 * @param sensor - The SensorInventory to check the providers of
 * @param selectedProviders - The selected providers to check for. This is optional
 */
const sensorProviderFilter = (sensor: SensorInventory, selectedProviders: InventoryProvider[]) =>
  !selectedProviders?.length ||
  sensor.providers.some(provider => selectedProviders.includes(provider));

/**
 * Checks whether a given SensorInventory is placed under a root ID, by comparing the rootId to the sensorInventory's path.
 * @param sensor - The SensorInventory to check the path of
 * @param rootId - The rootId to check for. This is optional, and if omitted, this function will always return true
 */
const sensorRootFilter = (sensor: SensorInventory, rootId: string | undefined) =>
  !rootId ? true : sensor.path.indexOf(rootId) !== -1;
